import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import {
  checkTermsAcceptance,
  checkPrivacyAcceptance,
  recordTermsAcceptance,
  recordPrivacyAcceptance,
  getTermsAndConditions,
  getPrivacyPolicy,
} from '../services/legalDocuments'
import Card from './Card'
import Button from './Button'
import { FileText, Shield, CheckCircle2, X, ExternalLink } from 'lucide-react'
import { useToast } from '../contexts/ToastContext'

const TermsAcceptanceModal = ({ organizationId, userId, onAccept }) => {
  const { success, error: showError } = useToast()
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [termsVersion, setTermsVersion] = useState('1.0')
  const [privacyVersion, setPrivacyVersion] = useState('1.0')
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [termsContent, setTermsContent] = useState(null)
  const [privacyContent, setPrivacyContent] = useState(null)
  // Track if user has already accepted in database (separate from checkbox state)
  const [alreadyAcceptedInDB, setAlreadyAcceptedInDB] = useState(false)

  useEffect(() => {
    checkAcceptances()
  }, [organizationId, userId])

  const checkAcceptances = async () => {
    if (!organizationId || !userId) return

    try {
      setLoading(true)
      
      // Check terms acceptance
      const termsResult = await checkTermsAcceptance(userId, organizationId)
      const termsAlreadyAccepted = termsResult.success && termsResult.data
      
      // Check privacy acceptance
      const privacyResult = await checkPrivacyAcceptance(userId, organizationId)
      const privacyAlreadyAccepted = privacyResult.success && privacyResult.data

      // If both already accepted in DB, mark as such and don't show modal
      if (termsAlreadyAccepted && privacyAlreadyAccepted) {
        setAlreadyAcceptedInDB(true)
        setTermsAccepted(true)
        setPrivacyAccepted(true)
      } else {
        // If not accepted in DB, don't pre-check the boxes
        // User needs to check them manually and click Accept
        setTermsAccepted(false)
        setPrivacyAccepted(false)
      }

      // Get current versions (you can fetch from database or use defaults)
      // For now, using default version
      setTermsVersion('1.0')
      setPrivacyVersion('1.0')
    } catch (error) {
      console.error('Error checking acceptances:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async () => {
    if (!termsAccepted || !privacyAccepted) {
      showError('Please accept both Terms and Conditions and Privacy Policy to continue.', {
        title: 'Acceptance Required',
      })
      return
    }

    try {
      setAccepting(true)

      // Check current acceptance status
      const currentTerms = await checkTermsAcceptance(userId, organizationId)
      const currentPrivacy = await checkPrivacyAcceptance(userId, organizationId)

      // Record terms acceptance if not already accepted
      if (!currentTerms.success || !currentTerms.data) {
        const termsResult = await recordTermsAcceptance(userId, organizationId, termsVersion)
        if (!termsResult.success) throw new Error('Failed to record terms acceptance')
      }

      // Record privacy acceptance if not already accepted
      if (!currentPrivacy.success || !currentPrivacy.data) {
        const privacyResult = await recordPrivacyAcceptance(userId, organizationId, privacyVersion)
        if (!privacyResult.success) throw new Error('Failed to record privacy acceptance')
      }

      success('Thank you for accepting our Terms and Conditions and Privacy Policy.', {
        title: 'Acceptance Recorded',
      })

      // Call onAccept callback
      if (onAccept) {
        onAccept()
      }
    } catch (error) {
      console.error('Error accepting terms:', error)
      const errorMessage = error.message || 'Failed to accept terms. Please try again.'
      showError(errorMessage, {
        title: 'Acceptance Failed',
      })
    } finally {
      setAccepting(false)
    }
  }

  const loadTermsContent = async () => {
    try {
      const result = await getTermsAndConditions()
      if (result.success) {
        setTermsContent(result.data)
      }
    } catch (error) {
      console.error('Error loading terms:', error)
    }
  }

  const loadPrivacyContent = async () => {
    try {
      const result = await getPrivacyPolicy()
      if (result.success) {
        setPrivacyContent(result.data)
      }
    } catch (error) {
      console.error('Error loading privacy policy:', error)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card variant="glass" className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading documents...</p>
        </Card>
      </div>
    )
  }

  // If both already accepted in database, don't show modal
  if (alreadyAcceptedInDB) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl"
        >
          <Card variant="glass" className="p-8 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                <Shield size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Terms & Privacy Policy
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please review and accept our Terms and Conditions and Privacy Policy to continue.
              </p>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <FileText size={24} className="text-primary-600 dark:text-primary-400" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Terms and Conditions</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Version {termsVersion}</p>
                  </div>
                </div>
                {termsAccepted && (
                  <CheckCircle2 size={24} className="text-green-600 dark:text-green-400" />
                )}
              </div>
              
              <div className="flex items-center gap-4 mb-3">
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  <ExternalLink size={14} />
                  Read Full Terms
                </a>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    I accept the Terms and Conditions
                  </span>
                </label>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-primary-600 dark:text-primary-400" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Privacy Policy</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Version {privacyVersion}</p>
                  </div>
                </div>
                {privacyAccepted && (
                  <CheckCircle2 size={24} className="text-green-600 dark:text-green-400" />
                )}
              </div>
              
              <div className="flex items-center gap-4 mb-3">
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  <ExternalLink size={14} />
                  Read Full Privacy Policy
                </a>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    I accept the Privacy Policy
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleAccept}
                disabled={!termsAccepted || !privacyAccepted || accepting}
                className="flex-1"
              >
                {accepting ? 'Accepting...' : 'Accept & Continue'}
              </Button>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
              By accepting, you agree to be bound by these terms and our privacy policy.
            </p>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default TermsAcceptanceModal

